<script setup lang="ts">
  import { debounce } from "@krainovsd/js-helpers";
  import { VButton, VCheckBox, VDivider, VInput } from "@krainovsd/vue-ui";
  import { computed, ref, useTemplateRef, watch } from "vue";
  import type { Ref } from "vue";
  import { useI18n } from "vue-i18n";
  import { CACHE_TTL, useCachedResource } from "@/entities/cache";
  import {
    fetchCampaignList,
    fetchCreativeList,
    fetchMailingTypeList,
    fetchOfferList,
    fetchTriggerList,
  } from "@/entities/filter-options";
  import type { FilterDimension, FilterOption } from "@/entities/filter-options";
  import { useReportStore } from "@/entities/report";
  import Accordion from "@/components/atoms/Accordion.vue";
  import VirtualList from "@/components/atoms/VirtualList.vue";
  import Search from "@/components/icons/Search.vue";
  import PickerButton from "@/components/molecules/PickerButton.vue";
  import { sortSelectedFirst } from "@/lib/sort-selected-first";

  // List virtualization tuning — keep this in sync with `.virtualItem` height.
  const ITEM_HEIGHT = 44;
  const SEARCH_DEBOUNCE_MS = 200;
  type ListExpose = { scrollToOffset: (offset: number) => void };

  // A pair of refs for an accordion's search box: `draft` is bound to the
  // VInput and updates on every keystroke; `search` is debounced and drives the
  // filtered list, so typing doesn't re-walk a large list on every character.
  // Returned as separate refs so they can be destructured into top-level refs
  // (template auto-unwrap only works on top-level refs, not nested ones).
  function useDebouncedSearch(onApply?: () => void) {
    const draft = ref("");
    const search = ref("");
    const apply = debounce((value: string) => {
      search.value = value;
      onApply?.();
    }, SEARCH_DEBOUNCE_MS);
    watch(draft, (value) => apply(value));
    return { draft, search };
  }

  const { t } = useI18n();
  const reportStore = useReportStore();
  const filters = computed(() => reportStore.currentFilters);

  const sheetOpen = ref(false);
  // Only one accordion is open at a time — opening one closes the others.
  const openAccordion = ref<FilterDimension | null>(null);

  // Drafts — re-seeded from the applied filters each time the sheet opens, so
  // edits left unconfirmed are discarded on reopen.
  const offersDraft = ref<number[]>([]);
  const triggersDraft = ref<number[]>([]);
  const creativesDraft = ref<number[]>([]);
  const campaignsDraft = ref<number[]>([]);
  const mailingTypesDraft = ref<number[]>([]);

  // List refs so we can snap the virtual scroller back to the top after a
  // debounced search rewrites the filtered list.
  const offersListRef = useTemplateRef<ListExpose | null>("offersListRef");
  const triggersListRef = useTemplateRef<ListExpose | null>("triggersListRef");
  const creativesListRef = useTemplateRef<ListExpose | null>("creativesListRef");
  const campaignsListRef = useTemplateRef<ListExpose | null>("campaignsListRef");

  // Per-accordion debounced search (Тип отправки has no search). Destructured
  // so each ref is a top-level ref and the template auto-unwraps it for
  // `v-model="offersSearchDraft"` etc.
  const { draft: offersSearchDraft, search: offersSearch } = useDebouncedSearch(() =>
    offersListRef.value?.scrollToOffset(0),
  );
  const { draft: triggersSearchDraft, search: triggersSearch } = useDebouncedSearch(() =>
    triggersListRef.value?.scrollToOffset(0),
  );
  const { draft: creativesSearchDraft, search: creativesSearch } = useDebouncedSearch(() =>
    creativesListRef.value?.scrollToOffset(0),
  );
  const { draft: campaignsSearchDraft, search: campaignsSearch } = useDebouncedSearch(() =>
    campaignsListRef.value?.scrollToOffset(0),
  );

  function seedDrafts() {
    offersDraft.value = [...filters.value.offers];
    triggersDraft.value = [...filters.value.triggers];
    creativesDraft.value = [...filters.value.creatives];
    campaignsDraft.value = [...filters.value.campaigns];
    mailingTypesDraft.value = [...filters.value.mailingTypes];
    // Reset both the draft and the applied search so the list shows everything
    // immediately, without waiting for the debounce to settle.
    offersSearchDraft.value = "";
    offersSearch.value = "";
    triggersSearchDraft.value = "";
    triggersSearch.value = "";
    creativesSearchDraft.value = "";
    creativesSearch.value = "";
    campaignsSearchDraft.value = "";
    campaignsSearch.value = "";
    openAccordion.value = null;
  }

  watch(sheetOpen, (open) => {
    if (open) seedDrafts();
  });

  // Applied count across all five dimensions, used to badge the trigger button.
  const appliedTotal = computed(
    () =>
      filters.value.offers.length +
      filters.value.triggers.length +
      filters.value.creatives.length +
      filters.value.campaigns.length +
      filters.value.mailingTypes.length,
  );

  const sheetTitle = computed(() => t("page.report.filterSheet.title", {}, { default: "Фильтры" }));
  const buttonValue = computed(() =>
    appliedTotal.value > 0 ? `${sheetTitle.value} (${appliedTotal.value})` : null,
  );

  // Lists scoped to providers refetch when the provider set changes; the key is
  // empty while the sheet is closed so the resources stay idle.
  const providerKey = computed(() => filters.value.providers.slice().sort().join(","));
  const NAMESPACE = "filter-options";

  function listKey(dim: FilterDimension, withProviders: boolean) {
    return computed(() =>
      sheetOpen.value ? `${dim}|${withProviders ? providerKey.value : "all"}` : "",
    );
  }

  const { data: offersList } = useCachedResource<FilterOption[]>({
    namespace: NAMESPACE,
    key: listKey("offers", true),
    ttl: CACHE_TTL.Default,
    strategy: "stale-while-revalidate",
    fetcher: () => fetchOfferList(filters.value.providers),
  });
  const { data: triggersList } = useCachedResource<FilterOption[]>({
    namespace: NAMESPACE,
    key: listKey("triggers", true),
    ttl: CACHE_TTL.Default,
    strategy: "stale-while-revalidate",
    fetcher: () => fetchTriggerList(filters.value.providers),
  });
  const { data: creativesList } = useCachedResource<FilterOption[]>({
    namespace: NAMESPACE,
    key: listKey("creatives", false),
    ttl: CACHE_TTL.Default,
    strategy: "stale-while-revalidate",
    fetcher: () => fetchCreativeList(),
  });
  const { data: campaignsList } = useCachedResource<FilterOption[]>({
    namespace: NAMESPACE,
    key: listKey("campaigns", true),
    ttl: CACHE_TTL.Default,
    strategy: "stale-while-revalidate",
    fetcher: () => fetchCampaignList(filters.value.providers),
  });
  const { data: mailingTypesList } = useCachedResource<FilterOption[]>({
    namespace: NAMESPACE,
    key: listKey("mailingTypes", false),
    ttl: CACHE_TTL.Default,
    strategy: "stale-while-revalidate",
    fetcher: () => fetchMailingTypeList(),
  });

  function filterByQuery(list: FilterOption[] | null, query: string): FilterOption[] {
    if (!list) return [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((item) => item.name.toLowerCase().includes(q));
  }

  // Each list surfaces the currently-checked items at the top, preserving the
  // underlying order otherwise.
  const filteredOffers = computed(() =>
    sortSelectedFirst(filterByQuery(offersList.value, offersSearch.value), offersDraft.value),
  );
  const filteredTriggers = computed(() =>
    sortSelectedFirst(filterByQuery(triggersList.value, triggersSearch.value), triggersDraft.value),
  );
  const filteredCreatives = computed(() =>
    sortSelectedFirst(
      filterByQuery(creativesList.value, creativesSearch.value),
      creativesDraft.value,
    ),
  );
  const filteredCampaigns = computed(() =>
    sortSelectedFirst(
      filterByQuery(campaignsList.value, campaignsSearch.value),
      campaignsDraft.value,
    ),
  );
  // Тип отправки has no search.
  const sendTypeItems = computed(() =>
    sortSelectedFirst(mailingTypesList.value ?? [], mailingTypesDraft.value),
  );

  const drafts: Record<FilterDimension, Ref<number[]>> = {
    offers: offersDraft,
    triggers: triggersDraft,
    creatives: creativesDraft,
    campaigns: campaignsDraft,
    mailingTypes: mailingTypesDraft,
  };

  function toggleDraft(dim: FilterDimension, id: number, checked: boolean) {
    const draft = drafts[dim];
    const next = new Set(draft.value);
    if (checked) next.add(id);
    else next.delete(id);
    draft.value = Array.from(next);
  }

  function setOpenAccordion(dim: FilterDimension, open: boolean) {
    // Single-open semantics: opening one collapses any other; closing leaves
    // everything collapsed.
    openAccordion.value = open ? dim : null;
  }

  function confirm() {
    reportStore.setCurrentFilters({
      ...filters.value,
      offers: [...offersDraft.value],
      triggers: [...triggersDraft.value],
      creatives: [...creativesDraft.value],
      campaigns: [...campaignsDraft.value],
      mailingTypes: [...mailingTypesDraft.value],
    });
    sheetOpen.value = false;
  }

  function cancel() {
    // Drafts are re-seeded on the next open; just close.
    sheetOpen.value = false;
  }
</script>

<template>
  <PickerButton
    v-model:open="sheetOpen"
    :value="buttonValue"
    :placeholder="sheetTitle"
    :title="sheetTitle"
    mode="sheet"
  >
    <div :class="$style.body">
      <Accordion
        :label="$t('page.report.filterSheet.offers', {}, { default: 'Оффер' })"
        :count="offersDraft.length"
        :open="openAccordion === 'offers'"
        @update:open="(open) => setOpenAccordion('offers', open)"
      >
        <div :class="$style.accordionBody">
          <VInput
            v-model="offersSearchDraft"
            shape="round"
            size="large"
            :placeholder="$t('page.report.filterSheet.searchPlaceholder', {}, { default: 'Поиск' })"
          >
            <template #prefix><Search /></template>
          </VInput>
          <VirtualList
            ref="offersListRef"
            :class="$style.virtualList"
            :items="filteredOffers"
            :item-size="ITEM_HEIGHT"
          >
            <template #default="{ item }">
              <div :class="$style.virtualItem">
                <VCheckBox
                  :model-value="offersDraft.includes(item.id)"
                  @update:model-value="
                    (checked) => toggleDraft('offers', item.id, checked ?? false)
                  "
                >
                  {{ item.name }}
                </VCheckBox>
              </div>
            </template>
          </VirtualList>
        </div>
      </Accordion>

      <Accordion
        :label="$t('page.report.filterSheet.triggers', {}, { default: 'Триггер' })"
        :count="triggersDraft.length"
        :open="openAccordion === 'triggers'"
        @update:open="(open) => setOpenAccordion('triggers', open)"
      >
        <div :class="$style.accordionBody">
          <VInput
            v-model="triggersSearchDraft"
            shape="round"
            size="large"
            :placeholder="$t('page.report.filterSheet.searchPlaceholder', {}, { default: 'Поиск' })"
          >
            <template #prefix><Search /></template>
          </VInput>
          <VirtualList
            ref="triggersListRef"
            :class="$style.virtualList"
            :items="filteredTriggers"
            :item-size="ITEM_HEIGHT"
          >
            <template #default="{ item }">
              <div :class="$style.virtualItem">
                <VCheckBox
                  :model-value="triggersDraft.includes(item.id)"
                  @update:model-value="
                    (checked) => toggleDraft('triggers', item.id, checked ?? false)
                  "
                >
                  {{ item.name }}
                </VCheckBox>
              </div>
            </template>
          </VirtualList>
        </div>
      </Accordion>

      <Accordion
        :label="$t('page.report.filterSheet.creatives', {}, { default: 'Креатив' })"
        :count="creativesDraft.length"
        :open="openAccordion === 'creatives'"
        @update:open="(open) => setOpenAccordion('creatives', open)"
      >
        <div :class="$style.accordionBody">
          <VInput
            v-model="creativesSearchDraft"
            shape="round"
            size="large"
            :placeholder="$t('page.report.filterSheet.searchPlaceholder', {}, { default: 'Поиск' })"
          >
            <template #prefix><Search /></template>
          </VInput>
          <VirtualList
            ref="creativesListRef"
            :class="$style.virtualList"
            :items="filteredCreatives"
            :item-size="ITEM_HEIGHT"
          >
            <template #default="{ item }">
              <div :class="$style.virtualItem">
                <VCheckBox
                  :model-value="creativesDraft.includes(item.id)"
                  @update:model-value="
                    (checked) => toggleDraft('creatives', item.id, checked ?? false)
                  "
                >
                  {{ item.name }}
                </VCheckBox>
              </div>
            </template>
          </VirtualList>
        </div>
      </Accordion>

      <Accordion
        :label="$t('page.report.filterSheet.campaigns', {}, { default: 'Кампания' })"
        :count="campaignsDraft.length"
        :open="openAccordion === 'campaigns'"
        @update:open="(open) => setOpenAccordion('campaigns', open)"
      >
        <div :class="$style.accordionBody">
          <VInput
            v-model="campaignsSearchDraft"
            shape="round"
            size="large"
            :placeholder="$t('page.report.filterSheet.searchPlaceholder', {}, { default: 'Поиск' })"
          >
            <template #prefix><Search /></template>
          </VInput>
          <VirtualList
            ref="campaignsListRef"
            :class="$style.virtualList"
            :items="filteredCampaigns"
            :item-size="ITEM_HEIGHT"
          >
            <template #default="{ item }">
              <div :class="$style.virtualItem">
                <VCheckBox
                  :model-value="campaignsDraft.includes(item.id)"
                  @update:model-value="
                    (checked) => toggleDraft('campaigns', item.id, checked ?? false)
                  "
                >
                  {{ item.name }}
                </VCheckBox>
              </div>
            </template>
          </VirtualList>
        </div>
      </Accordion>

      <Accordion
        :label="$t('page.report.filterSheet.mailingTypes', {}, { default: 'Тип отправки' })"
        :count="mailingTypesDraft.length"
        :open="openAccordion === 'mailingTypes'"
        @update:open="(open) => setOpenAccordion('mailingTypes', open)"
      >
        <div :class="$style.accordionBody">
          <VirtualList :class="$style.virtualList" :items="sendTypeItems" :item-size="ITEM_HEIGHT">
            <template #default="{ item }">
              <div :class="$style.virtualItem">
                <VCheckBox
                  :model-value="mailingTypesDraft.includes(item.id)"
                  @update:model-value="
                    (checked) => toggleDraft('mailingTypes', item.id, checked ?? false)
                  "
                >
                  {{ item.name }}
                </VCheckBox>
              </div>
            </template>
          </VirtualList>
        </div>
      </Accordion>

      <VDivider :class="$style.divider" />

      <div :class="$style.footer">
        <VButton type="text" @click="cancel">
          {{ $t("page.report.filterSheet.cancel", {}, { default: "Отменить" }) }}
        </VButton>
        <VButton type="primary" :class="$style.confirmButton" @click="confirm">
          {{ $t("page.report.filterSheet.apply", {}, { default: "Применить" }) }}
        </VButton>
      </div>
    </div>
  </PickerButton>
</template>

<style lang="scss" module>
  .body {
    width: 100%;

    display: flex;
    flex-direction: column;
  }

  .accordionBody {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  // Virtualized list height — items inside it must each be ITEM_HEIGHT px tall
  // so the virtualizer's offset math stays in sync with the rendered rows.
  .virtualList {
    height: 320px;
  }

  .virtualItem {
    height: 44px;
    padding: 10px 8px;

    display: flex;
    align-items: center;
  }

  .divider {
    margin: 12px 0;
  }

  .footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
  }

  .confirmButton {
    background: var(--brand);
  }
</style>
